import React, { useState } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';
import DocumentPreview from '../../../Components/Ui/DocumentPreview';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import { useNavigate, useParams } from 'react-router-dom';
import toastService from '../../../services/toastService';
import { useGetProductByIdQuery, useGetAllCategoriesQuery } from '../../../app/services/productApi';
import { useAddToCatalogMutation } from '../../../app/services/catalogApi';
import { useSelector } from 'react-redux';
import InputField from '../../../Components/Ui/InputField';
import TierRow from '../../../Components/Ui/TierRow';
import useAddProductForm from '../../../hooks/useAddProductForm'; // Import the custom hook
import Loader from '../../../Components/Ui/Loader';

const SellerNewProduct = () => {
  const { id: productId } = useParams();
  const { user } = useSelector((state) => state?.userInfo);
  const { brandId: ownedBrandId } = useSelector((state) => state.myBrand);
  const sellerId = user?._id || user?.id;

  const {
    data: { data: product } = { data: {} },
    isLoading: productLoading,
    isError: productError,
  } = useGetProductByIdQuery(productId);
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategoriesQuery(true);
  const navigate = useNavigate();
  const [addToCatalog, { isLoading: isAddingToCatalog }] = useAddToCatalogMutation();
  const [selectedImage, setSelectedImage] = useState(null);

  // Check if seller is approved for this product's brand
  const productBrandId = product?.brand?._id || product?.brand;

  // Bypass if it's the seller's own brand
  const isOwnBrand = ownedBrandId && productBrandId === ownedBrandId;

  const brandDeal = user?.brandDeal?.find(
    (deal) => (deal.brand?._id || deal.brand) === productBrandId,
  );
  const isApprovedForBrand = isOwnBrand || brandDeal?.status === 'approved';

  React.useEffect(() => {
    if (product?.brand && !isApprovedForBrand) {
      toastService.info(
        `You are not yet approved to sell products for brand: ${product.brand.name || 'this brand'}. Please wait for admin approval.`,
        {
          toastId: 'brand-not-approved',
        },
      );
    }
  }, [product, isApprovedForBrand]);

  // Use the custom hook
  const {
    formData,
    handleInputChange,
    tieredPricingEnabled,
    bulkPricingTiers,
    handleTieredPricingToggle,
    addTier,
    removeTier,
    updateTier,
    multiplier,
    setMultiplier,
    moqLoose,
    validateForm,
    getSubmissionData,
    categories,
    subcategories,
    keyFeatures,
    productImages,
    currentTotalMoq,
    packagingTypeDefaults,
  } = useAddProductForm(product, categoriesData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const submissionData = getSubmissionData(productId, sellerId);
      await addToCatalog(submissionData).unwrap();
      toastService.success('Product added to catalog successfully!');
      navigate('/seller/catalog');
    } catch (error) {
      toastService.error(error.data?.message || 'Failed to add product to catalog');
    }
  };

  if (productLoading || categoriesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (productError || !product) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">Error loading product details</p>
      </div>
    );
  }

  return (
    <div className="pt-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 lg:px-8">
        <h2 className="text-lg xl:text-2xl font-bold">Add Product to Catalog</h2>
        <Button onClick={() => navigate(-1)} className="mt-4 sm:mt-0 w-full sm:w-auto">
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
      </div>

      <Card className="mt-2 px-4 sm:px-6 py-6">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Product Information Section */}
          <div>
            <h3 className="text-lg font-medium tertiary-text-color mb-4">Product Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <InputField
                id="name"
                name="name"
                label="Product Title"
                value={formData.name}
                disabled
              />
              <InputField
                id="mrp"
                name="mrp"
                label="MRP"
                type="number"
                value={formData.mrp}
                disabled
              />
              <InputField
                id="productCode"
                name="productCode"
                label="Product Code"
                value={formData.productCode}
                disabled
              />
              <InputField id="sku" name="sku" label="SKU" value={formData.sku} disabled />
              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-secondary">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  disabled
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                />
              </div>
              <InputField
                id="category"
                name="category"
                label="Category"
                value={categories.find((c) => c._id === formData.category)?.name || ''}
                disabled
              />
              <InputField
                id="subcategory"
                name="subcategory"
                label="Subcategory"
                value={subcategories.find((s) => s._id === formData.subcategory)?.name || ''}
                disabled
              />
            </div>
          </div>

          {/* Your Catalog Details Section */}
          <div>
            <h3 className="text-lg font-medium tertiary-text-color mb-4">Pricing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <InputField
                id="buyingPrice"
                name="buyingPrice"
                label="Cost of Goods (Per unit inclusive of tax)"
                helperText="The amount you are spending to produce/buy this product"
                type="text"
                value={formData.buyingPrice}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
              />
              <InputField
                id="sellingPrice"
                name="sellingPrice"
                label="Selling Price (Per unit inclusive of tax)"
                type="text"
                value={formData.sellingPrice}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
                required
              />
              <InputField
                id="discount"
                name="discount"
                label="Discount (%)"
                type="text"
                value={formData.discount}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value) || e.target.value === '') {
                    handleInputChange(e);
                  }
                }}
                min="0"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:gap-6 items-start mt-6">
              {/* Selling Condition Dropdown */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="selectedPackagingType"
                  className="block text-sm font-medium text-secondary mb-1"
                >
                  Selling Condition{' '}
                  <span className="text-xs font-normal">
                    (Select how you want to sell this product)
                  </span>{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="selectedPackagingType"
                  name="selectedPackagingType"
                  value={formData.selectedPackagingType}
                  onChange={handleInputChange}
                  required
                  className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                >
                  <option value="loose">Individual Unit (Loose packing)</option>
                  <option value="standard">Standard Package</option>
                  <option value="master">Master Package (Shipping carton)</option>
                </select>
              </div>

              {/* MOQ Calculation */}
              {formData.selectedPackagingType && (
                <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Base MOQ Value */}
                  <div className="flex flex-col col-span-1">
                    <span className="text-sm font-medium text-secondary mb-1">&nbsp;</span>
                    <div className="flex items-center">
                      <span className="p-2 border border-gray-200 bg-gray-50 rounded-md text-sm w-full text-center">
                        {formData.selectedPackagingType === 'loose'
                          ? moqLoose
                          : packagingTypeDefaults[formData.selectedPackagingType]}
                      </span>
                      <span className="text-lg font-medium ml-2">×</span>
                    </div>
                  </div>

                  {/* Multiplier Input */}
                  <div className="flex flex-col col-span-2">
                    <label className="block text-sm font-medium text-secondary mb-1 truncate">
                      MOQ <span className="text-xs font-normal">(Smallest order size)</span>
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min={1}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        value={multiplier}
                        onChange={(e) => setMultiplier(Math.max(1, parseInt(e.target.value || 1)))}
                      />
                      <span className="text-sm text-gray-600 ml-2 whitespace-nowrap">
                        {
                          {
                            loose: 'pc/s',
                            standard: 'pkg/s',
                            master: 'carton/s',
                          }[formData.selectedPackagingType]
                        }
                      </span>
                      <span className="text-lg font-medium ml-2">=</span>
                    </div>
                  </div>

                  {/* Total MOQ Display */}
                  <div className="flex flex-col col-span-1">
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Total pieces
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        readOnly
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 text-center"
                        value={currentTotalMoq}
                      />
                      <span className="text-sm text-gray-600 ml-2">pcs</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* NEW: Tiered Pricing Section */}
          <div>
            {/* Header */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-medium tertiary-text-color text-center sm:text-left">
                Bulk/Tiered Pricing
              </h3>

              <div className="flex items-center justify-center sm:justify-end space-x-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={tieredPricingEnabled}
                    onChange={handleTieredPricingToggle}
                  />
                  <span
                    className={`w-11 h-6 flex items-center rounded-full transition-colors duration-300 ${
                      tieredPricingEnabled ? 'bg-[#E95B21]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 tertiary-background rounded-full shadow transform transition-transform duration-300 ${
                        tieredPricingEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </span>
                </label>
                <span className="text-sm font-medium text-secondary">Enable Bulk Pricing</span>
              </div>
            </div>

            {/* Description */}
            <div className="text-sm text-gray-600 mb-4 leading-relaxed text-center sm:text-left">
              Set different prices for bulk orders. Customers ordering above the minimum quantity
              for each tier will get the tier pricing.
            </div>

            {/* Tier Table */}
            {tieredPricingEnabled && (
              <div className="space-y-4">
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full min-w-[720px] text-sm text-left border-collapse">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-3 font-medium text-secondary">Min Quantity</th>
                        <th className="px-4 py-3 font-medium text-secondary">Unit</th>
                        <th className="px-4 py-3 font-medium text-secondary">
                          Selling Price (Per unit inclusive of tax)
                        </th>
                        <th className="px-4 py-3 font-medium text-secondary">Discount (%)</th>
                        <th className="px-4 py-3 font-medium text-secondary">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPricingTiers.map((tier, index) => (
                        <TierRow
                          key={index}
                          tier={tier}
                          index={index}
                          onUpdate={updateTier}
                          onRemove={removeTier}
                          productMrp={parseFloat(formData.mrp)}
                          baseSellingPrice={parseFloat(formData.sellingPrice)}
                          allTiers={bulkPricingTiers}
                          canRemove={bulkPricingTiers.length > 1}
                          disableDiscount={false}
                          hideUnit={false}
                          unit={
                            {
                              loose: 'pc/s',
                              standard: 'pkg/s',
                              master: 'carton/s',
                            }[formData.selectedPackagingType]
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add Tier Button */}
                <Button
                  type="button"
                  onClick={addTier}
                  className="flex items-center space-x-2 text-sm px-4 py-2 w-full sm:w-auto"
                  variant="outline"
                >
                  <Plus size={16} />
                  <span>Add Tier</span>
                </Button>

                {/* Pricing Preview */}
                {bulkPricingTiers.some((tier) => tier.minQuantity && tier.price) && (
                  <div className="secondary-background border border-color rounded-md p-4 mt-4">
                    <h4 className="text-sm font-medium text-primary mb-2">Pricing Preview:</h4>
                    <ul className="text-sm text-primary space-y-1">
                      <li>
                        • Base price: ₹{formData.sellingPrice} (for orders below bulk quantities)
                      </li>
                      {bulkPricingTiers
                        .filter((tier) => tier.minQuantity && tier.price)
                        .sort((a, b) => parseInt(a.minQuantity) - parseInt(b.minQuantity))
                        .map((tier, index) => (
                          <li key={index}>
                            • {tier.minQuantity}+ pieces: ₹{tier.price} ({tier.discountPercentage}%
                            off MRP)
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Key Features Section */}
          <div className="mt-6">
            <h3 className="text-base font-medium tertiary-text-color mb-2">Key Features</h3>
            {keyFeatures.length > 0 ? (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 font-medium text-secondary">Title</th>
                      <th className="px-4 py-2 font-medium text-secondary">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keyFeatures.map((feature, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={feature.title}
                            disabled
                            className="w-full px-3 py-1 border rounded-md text-sm bg-gray-100 cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={feature.description}
                            disabled
                            className="w-full px-3 py-1 border rounded-md text-sm bg-gray-100 cursor-not-allowed"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mt-2">No key features available.</p>
            )}
          </div>

          {/* Product Images Section */}
          <div>
            <h3 className="text-base font-medium tertiary-text-color mb-2">Product Images</h3>
            {productImages.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {productImages.map((img, i) => (
                  <div
                    key={img._id || i}
                    className="relative w-full h-32 rounded overflow-hidden border"
                  >
                    <img
                      src={img}
                      alt={img.alt || formData.name || `Product ${i + 1}`}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={() => setSelectedImage(img)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 mt-2">No product images available.</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center md:items-end space-y-2 pt-4">
            {!isApprovedForBrand && product?.brand && (
              <p className="text-sm text-red-500 font-medium">
                Verification for brand "{product.brand.name || 'this brand'}" is pending.
              </p>
            )}
            <div className="flex md:justify-end space-x-4 text-sm justify-center">
              <Button
                type="submit"
                className="w-full sm:w-auto px-8 py-2 text-sm"
                disabled={isAddingToCatalog || !isApprovedForBrand}
              >
                {!isApprovedForBrand
                  ? 'Approval Pending'
                  : isAddingToCatalog
                    ? 'Adding...'
                    : 'Add to Catalog'}
              </Button>
            </div>
          </div>
        </form>

        <DocumentPreview
          isOpen={!!selectedImage}
          fileUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
          fileType={selectedImage ? selectedImage.split('.').pop() : ''}
        />
      </Card>
    </div>
  );
};

export default SellerNewProduct;
